/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import './index.less';

import { Flex, Icon, Space, useColorMode } from '@tonic-one/react';
import { CloseSIcon } from '@tonic-one/react-icons';
import { useCombobox, useMultipleSelection } from 'downshift';
import isNumber from 'lodash/isNumber';
import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Criterion, SearchCriteria, SearchOptionItem } from 'src/types/log';

import SingleCriteria from './SingleCriterion';
import { menuMultipleStyles } from './const';

const selectedItemStyles = {
  marginLeft: '8px',
  backgroundColor: '#424242',
  borderRadius: '3px',
};

const searchBoxStyles = {
  border: '1px solid #5E5E5E',
  borderRadius: '3px',
  width: '100%',
};

const comboboxStyles = {
  display: 'inline-block',
  marginLeft: '5px',
  padding: '0px 8px',
  flexGrow: '1',
};

function DropdownMultipleCombobox({
  logType,
  criteria,
  setCriteria,
  searchFields,
}: SearchCriteria) {
  const { t } = useTranslation();
  // console.log('Search criteria criteria:', criteria);

  const elements: SearchOptionItem[] = React.useMemo(
    () =>
      searchFields.map((item) => {
        const result = {
          title: item.field,
          key: item.field,
        };
        if (logType === 'urlTracking' && item.field === 'action') {
          result.title =
            t(`log.fields.${item.field}`) +
            ': ' +
            t('dashboard.timeOfClickProtection.restricted');
        } else {
          let title = '';
          title = t(`log.fields.${item.field}`);
          if (title === `log.fields.${item.field}`) {
            title = item.field;
          }
          result.title = title;
        }
        return result;
      }),
    [searchFields]
  );

  const [inputValue, setInputValue] = React.useState('');
  const [isAnyCriteriaFocus, setTotalFocus] = React.useState(false);

  const refInput = useRef<HTMLInputElement>(null);
  const getFilteredItems = useCallback(
    (inputValue: string) => {
      const lowerCasedInputValue = inputValue.toLowerCase();
      return elements.filter(
        (element) => ~element.title.toLowerCase().indexOf(lowerCasedInputValue)
      );
    },
    [elements]
  );
  const onSingleCriteriaComplete = useCallback(() => {
    setTimeout(() => {
      refInput.current?.focus();
    }, 100);
  }, []);
  const items = React.useMemo(
    () => getFilteredItems(inputValue),
    [inputValue, searchFields]
  );
  const setSingleCriteriaFocus = React.useCallback(
    (isFocus: boolean, index: number) => {
      setTotalFocus(isFocus);
      const items = criteria;
      const len = items.length;
      const item = items[index];
      item.focus = isFocus;
      const newItems = [
        ...items.slice(0, index),
        item,
        ...items.slice(index + 1, len),
      ];
      setCriteria(newItems);
    },
    [criteria]
  );
  const allFieldTitle = t(`log.fields.all`);
  const { getSelectedItemProps, getDropdownProps, removeSelectedItem } =
    useMultipleSelection({
      selectedItems: criteria,
      onStateChange({ selectedItems: newSelectedItems, type }) {
        // console.log(
        //   'Search criteria multiple selection on state change:',
        //   type,
        //   newSelectedItems
        // );
        switch (type) {
          case useMultipleSelection.stateChangeTypes
            .SelectedItemKeyDownBackspace:
          case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownDelete:
          case useMultipleSelection.stateChangeTypes.DropdownKeyDownBackspace:
          case useMultipleSelection.stateChangeTypes.FunctionRemoveSelectedItem:
            setCriteria(newSelectedItems as Criterion[]);
            break;
          case useMultipleSelection.stateChangeTypes.SelectedItemClick:
            //console.log(criteria, newSelectedItems);
            setCriteria(newSelectedItems as Criterion[]);
            break;
          default:
            break;
        }
      },
      stateReducer(state, actionAndChanges) {
        const { type, changes } = actionAndChanges;
        const { activeIndex, selectedItems } = changes;
        // console.log('Search criteria multiple state reducer:', type, changes);
        let newSelectedItems = [];
        switch (type) {
          case useMultipleSelection.stateChangeTypes
            .SelectedItemKeyDownNavigationPrevious:
          case useMultipleSelection.stateChangeTypes
            .SelectedItemKeyDownNavigationNext:
            if (isAnyCriteriaFocus) {
              return {
                ...changes,
                activeIndex,
              };
            } else {
              return changes;
            }
          case useMultipleSelection.stateChangeTypes.SelectedItemClick:
            // click one criterion, change the focus status
            if (Array.isArray(selectedItems)) {
              newSelectedItems = [...selectedItems];
              if (
                isNumber(activeIndex) &&
                newSelectedItems.length >= activeIndex + 1
              ) {
                newSelectedItems[activeIndex].focus = true;
              }
              return {
                ...changes,
                selectedItems: newSelectedItems,
              };
            }
            return changes;
          case useMultipleSelection.stateChangeTypes
            .SelectedItemKeyDownBackspace:
          case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownDelete:
            return state;
          default:
            return changes;
        }
      },
    });
  const {
    isOpen,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
  } = useCombobox({
    items,
    inputValue,
    itemToString: (item) => {
      return item ? item.title : '';
    },
    selectedItem: null,
    stateReducer(state, actionAndChanges) {
      // console.log(
      //   'Search criteria combobox state reducer:',
      //   actionAndChanges.type,
      //   actionAndChanges
      // );
      const { changes, type } = actionAndChanges;

      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
          if (changes.selectedItem === null) {
            // no key is matched, match 'all field'
            return {
              ...changes,
              selectedItem: {
                key: 'all',
                title: changes.inputValue || '', // use title pass the input value, so onStateChange can get it
              },
              isOpen: false,
              highlightedIndex: 0,
            };
          }
          return {
            ...changes,
            ...(changes.selectedItem && { isOpen: false, highlightedIndex: 0 }),
          };
        case useCombobox.stateChangeTypes.ItemClick:
        case useCombobox.stateChangeTypes.InputBlur:
          return {
            ...changes,
            ...(changes.selectedItem && { isOpen: false, highlightedIndex: 0 }),
          };
        default:
          return changes;
      }
    },
    onStateChange({ inputValue: newInputValue, type, selectedItem }) {
      // console.log(
      //   'multiple combobox on state change:',
      //   type,
      //   newInputValue,
      //   selectedItem
      // );
      const newSelectedItem = {
        field: selectedItem?.key || '',
        value: '',
        focus: true,
        label: selectedItem?.title || '',
      };
      // when the input match no options, will treat it as allField, and stateReducer pass the input value as selectedItem title
      // If user input match nothing, the selectedItem.title will be the input value, newSelectedItem.value is selectedItem.title, complete this criterion
      // If user input match 'All Field', thie selectedItem.title will be 'All Field', newSelectedItem.value is '', wait user to input
      if (newSelectedItem?.field === 'all') {
        newSelectedItem.value =
          selectedItem?.title === allFieldTitle
            ? ''
            : selectedItem?.title || '';
        newSelectedItem.label = allFieldTitle;
        newSelectedItem.focus =
          selectedItem?.title === allFieldTitle ? true : false;
      }
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
          if (newSelectedItem.field === 'undefined') {
            // do not change anything when nothing is selected
          } else {
            setCriteria([...criteria, newSelectedItem]);
          }
          // clear input, if there is still any
          setInputValue('');
          break;
        case useCombobox.stateChangeTypes.ItemClick:
          if (logType === 'urlTracking' && newSelectedItem.field === 'action') {
            newSelectedItem.label = t(`log.fields.action`);
            newSelectedItem.value = t(
              'dashboard.timeOfClickProtection.restricted'
            );
          }
          setCriteria([...criteria, newSelectedItem]);
          // clear input, if there is still any
          setInputValue('');
          break;
        case useCombobox.stateChangeTypes.InputChange:
          setInputValue(newInputValue as string);
          break;
        default:
          break;
      }
    },
  });
  const SingleTag = ({ item, style }: { item: Criterion; style: any }) => {
    const { label, value } = item;

    return (
      <Flex
        style={{ ...style }}
        className="tagStyle"
        align="center"
        display="inline-block"
      >
        <label>{label}&nbsp;:&nbsp;</label>
        <Space width="10px" />
        {value}
      </Flex>
    );
  };
  const [isMenuOpen, setMenuOpen] = React.useState(false);
  const onClearAll = useCallback((e: any) => {
    e.stopPropagation();
    setCriteria([]);
  }, []);
  //console.log('Criteria render:', criteria, items);

  useEffect(() => {
    // console.log('open status change:', isAnyCriteriaFocus, isOpen);
    setMenuOpen(isOpen && !isAnyCriteriaFocus);
  }, [isAnyCriteriaFocus, isOpen]);

  const [colorMode] = useColorMode();
  const backgroundColor = colorMode === 'dark' ? '#424242' : '#f2f2f2';

  return (
    <div style={searchBoxStyles}>
      <div className="comboboxWrapper">
        {criteria.map((selectedItem, index) => {
          const editable = !(
            logType === 'urlTracking' && selectedItem.field === 'action'
          );
          return (
            <span
              style={selectedItemStyles}
              key={`selected-item-${index}`}
              {...getSelectedItemProps({ selectedItem, index })}
            >
              {selectedItem.focus && editable ? (
                <SingleCriteria
                  item={selectedItem}
                  style={{
                    display: 'inline-block',
                    borderRadius: 3,
                    padding: '4px 0px 4px 12px',
                    backgroundColor,
                  }}
                  inputHandler={(item: Criterion) => {
                    const len = criteria.length;
                    const newItems = [
                      ...criteria.slice(0, index),
                      item,
                      ...criteria.slice(index + 1, len),
                    ];
                    setCriteria(newItems);
                  }}
                  setFocus={(isFocus: boolean) =>
                    setSingleCriteriaFocus(isFocus, index)
                  }
                  onComplete={onSingleCriteriaComplete}
                />
              ) : (
                <div
                  className="flex"
                  style={{
                    display: 'inline-block',
                    borderRadius: 3,
                    padding: '4px 0px 4px 12px',
                    backgroundColor,
                  }}
                >
                  <SingleTag
                    item={selectedItem}
                    style={{
                      backgroundColor,
                    }}
                  />
                  <span
                    className="selectedItemCloseIcon"
                    style={{
                      backgroundColor,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      removeSelectedItem(selectedItem);
                    }}
                  >
                    <Icon as={CloseSIcon} marginBottom="2px" />
                  </span>
                </div>
              )}
            </span>
          );
        })}
        <div style={comboboxStyles} className="combobox">
          <input
            {...getInputProps({
              ...getDropdownProps({
                preventKeyAction: isMenuOpen,
                ref: refInput,
              }),
              onChange: (e: any) => {
                setInputValue(e.target.value);
              },
            })}
            style={{
              border: '0px',
              width: '100%',
              maxWidth: '100%',
              background: colorMode === 'dark' ? '#212121' : '',
            }}
            placeholder={
              criteria.length ? undefined : t(`log.placeholders.searchCriteria`)
            }
          />
        </div>
        {criteria.length ? (
          <div className="clearAllIcon" onClick={onClearAll}>
            <Icon as={CloseSIcon} />
          </div>
        ) : null}
      </div>
      <ul {...getMenuProps()} style={menuMultipleStyles}>
        {isMenuOpen &&
          items.map((item, index) => (
            <li
              className={
                highlightedIndex === index ? 'optionItemSelected' : 'optionItem'
              }
              style={{
                color: colorMode === 'dark' ? '#F2F2F2' : '#212121',
                background: colorMode === 'dark' ? '#303030' : '#f2f2f2',
              }}
              key={item.key}
              {...getItemProps({ item, index })}
            >
              {item.title}
            </li>
          ))}
      </ul>
    </div>
  );
}

export default React.memo(DropdownMultipleCombobox);
